import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { en } from "@/i18n/en";
import { pt } from "@/i18n/pt";
import { PAGES, applyHead, pageForPath } from "@/seo/head";

const DICTS = { en, pt };
const LangContext = createContext(null);

const lookup = (dict, key) => key.split(".").reduce((o, k) => (o == null ? o : o[k]), dict);

export function LangProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const match = pageForPath(location.pathname);
  const [pref, setPref] = useState(() => (localStorage.getItem("lang") === "pt" ? "pt" : "en"));
  // Public SEO pages take their language from the URL; app pages use the saved preference
  const lang = match ? match.lang : location.pathname.startsWith("/pt/") ? "pt" : pref;

  useEffect(() => {
    if (match && match.lang !== pref) { localStorage.setItem("lang", match.lang); setPref(match.lang); }
  }, [match?.lang]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { applyHead(location.pathname, lang); }, [location.pathname, lang]);

  const setLang = (l) => {
    localStorage.setItem("lang", l);
    setPref(l);
    if (match) navigate(PAGES[match.key].paths[l] + location.search);
  };

  const t = useCallback((key, vars) => {
    let v = lookup(DICTS[lang], key) ?? lookup(en, key) ?? key;
    if (typeof v === "string" && vars) Object.entries(vars).forEach(([k, x]) => { v = v.replace(`{${k}}`, x); });
    return v;
  }, [lang]);

  // Backend sends English messages; translate known ones
  const tErr = useCallback((msg) => (msg ? DICTS[lang].errors[msg] || msg : DICTS[lang].errors.generic), [lang]);
  const href = useCallback((key) => PAGES[key].paths[lang], [lang]);
  const toolHref = useCallback((slug) => PAGES[`tool_${slug}`].paths[lang], [lang]);
  const locale = lang === "pt" ? "pt-BR" : "en-US";

  return <LangContext.Provider value={{ lang, setLang, t, tErr, locale, href, toolHref }}>{children}</LangContext.Provider>;
}

export const useLang = () => useContext(LangContext);
